import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap } from 'lucide-react';
import useLandingPages from '../../hooks/useLandingPages';
import { GUIDE_CATEGORIES, GuideCategory, LandingPageSummary, landingPath } from '../../services/landingPageService';
import ArrowScrollRow from '../common/ArrowScrollRow';
import { currentLang, t } from '../../i18n';

// ============================================
// RatgeberTeaser (26.10.2026, Marcel): ALLE Ratgeber-Seiten als horizontale
// Sektion auf der Startseite unter „Hilfe & Anleitungen“ – jeder Link steht
// im DOM (Google findet die neuen Seiten über die Startseite). Muster wie
// HelpArticlesTeaser: Karten mit Kategorie-Kicker, Titel, Untertitel, Pfeile.
// ============================================

export const RATGEBER_CARD_WIDTH = 260;
export const RATGEBER_CARD_GAP = 12;
export const RATGEBER_SCROLL_STEP = RATGEBER_CARD_WIDTH + RATGEBER_CARD_GAP;

const categoryLabel = (c: GuideCategory | null): string => t(`web.page.landing-hub-page.category-${c ?? 'einstieg'}`);

/** Reihenfolge: Kategorien wie im Hub, innerhalb nach sortOrder (pure) */
export const orderGuidePages = (pages: LandingPageSummary[]): LandingPageSummary[] =>
  GUIDE_CATEGORIES.flatMap((c) => pages.filter((p) => (p.category ?? 'einstieg') === c).sort((a, b) => a.sortOrder - b.sortOrder));

const RatgeberTeaser: React.FC<{ className?: string }> = ({ className }) => {
  const lang = currentLang();
  const { pages } = useLandingPages(lang, 'guide');
  const items = orderGuidePages(pages);
  if (items.length === 0) return null;

  return (
    <section aria-label={t('web.component.ratgeber-teaser.titel')} data-testid="ratgeber-teaser" className={className}>
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.ratgeber-teaser.titel')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">{t('web.component.ratgeber-teaser.untertitel', { count: items.length })}</p>
        </div>
        <Link to={landingPath(null, lang, 'guide')} className="mr-1 flex shrink-0 items-center gap-1 text-xs font-semibold text-gray-400 transition-colors hover:text-[#3fced1]" data-testid="ratgeber-teaser-all">
          {t('web.component.ratgeber-teaser.alle-ratgeber')} <ArrowRight size={13} />
        </Link>
      </div>

      <ArrowScrollRow overlay as="ul" testId="ratgeber-teaser" wrapperTestId="ratgeber-teaser-rail" step={RATGEBER_SCROLL_STEP} compact scrollClassName="flex snap-x snap-mandatory gap-3 pb-1">
        {items.map((p) => (
          <li key={p.slug} className="shrink-0 snap-start" style={{ width: RATGEBER_CARD_WIDTH }}>
            <Link
              to={landingPath(p.slug, lang, 'guide')}
              data-testid={`ratgeber-teaser-${p.slug}`}
              className="group flex h-full min-h-[150px] flex-col rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 transition hover:border-[#3fced1]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
            >
              <span className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#3fced1]">
                <GraduationCap size={12} aria-hidden="true" />
                <span className="truncate">{categoryLabel(p.category)}</span>
              </span>
              <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-white">{p.title}</h3>
              {p.subtitle && <p className="mt-1.5 line-clamp-3 text-[13px] leading-relaxed text-gray-400">{p.subtitle}</p>}
            </Link>
          </li>
        ))}
      </ArrowScrollRow>
    </section>
  );
};

export default RatgeberTeaser;
